<template>
  <div class="admin-commission-rates">
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          <feather-icon
            icon="PercentIcon"
            size="20"
            class="mr-1"
          />
          Taux de Commission
        </h4>
        <b-button
          variant="primary"
          size="sm"
          @click="showCreateModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-1"
          />
          Nouveau Taux
        </b-button>
      </b-card-header>

      <b-card-body>
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="4">
            <b-form-group label="Collaborateur">
              <b-form-select
                v-model="filters.salesperson"
                :options="salespersonOptions"
                @change="loadRates"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
        </div>

        <b-table
          v-else
          :items="rates"
          :fields="fields"
          responsive
          hover
        >
          <template #cell(salesperson_name)="data">
            <span class="font-weight-bold">{{ data.item.salesperson_name }}</span>
            <br>
            <small class="text-muted">{{ data.item.salesperson_email }}</small>
          </template>

          <template #cell(rate)="data">
            <span class="font-weight-bold text-success">
              {{ formatAmount(data.item.rate) }} DT
            </span>
          </template>

          <template #cell(period)="data">
            {{ formatDate(data.item.start_date) }} - {{ formatDate(data.item.end_date) }}
          </template>

          <template #cell(status)="data">
            <b-badge :variant="getStatusVariant(data.item)">
              {{ getStatusLabel(data.item) }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="canDelete(data.item)"
              variant="outline-danger"
              size="sm"
              @click="confirmDelete(data.item)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- Create Modal -->
    <b-modal
      v-model="showCreateModal"
      title="Nouveau Taux de Commission"
      hide-footer
    >
      <b-form @submit.prevent="createRate">
        <b-form-group label="Collaborateur">
          <b-form-select
            v-model="newRate.salesperson"
            :options="salespersonOptions"
            required
          />
        </b-form-group>

        <b-form-group label="Taux (DT par commande)">
          <b-form-input
            v-model="newRate.rate"
            type="number"
            step="0.001"
            min="0"
            required
          />
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-group label="Date de début">
              <b-form-input
                v-model="newRate.start_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Date de fin">
              <b-form-input
                v-model="newRate.end_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end gap-2">
          <b-button
            variant="outline-secondary"
            @click="showCreateModal = false"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="creating"
          >
            {{ creating ? 'Création...' : 'Créer' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BForm,
  BModal,
} from 'bootstrap-vue'

export default {
  name: 'AdminCommissionRates',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BForm,
    BModal,
  },
  data() {
    return {
      loading: false,
      creating: false,
      rates: [],
      salespeople: [],
      showCreateModal: false,
      filters: {
        salesperson: '',
      },
      newRate: {
        salesperson: '',
        rate: '',
        start_date: '',
        end_date: '',
      },
      fields: [
        { key: 'salesperson_name', label: 'Commercial' },
        { key: 'rate', label: 'Taux', class: 'text-center' },
        { key: 'period', label: 'Période' },
        { key: 'status', label: 'Statut', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
    }
  },
  computed: {
    salespersonOptions() {
      const options = [{ value: '', text: 'Tous les collaborateurs' }]
      this.salespeople.forEach(sp => {
        options.push({ value: sp.id, text: sp.username })
      })
      return options
    },
  },
  mounted() {
    this.loadSalespeople()
    this.loadRates()
  },
  methods: {
    async loadSalespeople() {
      try {
        const response = await axios.get('/api/auth/users/', {
          params: { can_receive_commission: true },
        })
        this.salespeople = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading salespeople:', error)
      }
    },
    async loadRates() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.salesperson) {
          params.salesperson = this.filters.salesperson
        }
        const response = await axios.get('/api/commissions/commission-rates/', { params })
        this.rates = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading rates:', error)
      } finally {
        this.loading = false
      }
    },
    async createRate() {
      this.creating = true
      try {
        await axios.post('/api/commissions/commission-rates/', this.newRate)
        this.$bvToast.toast('Taux de commission créé avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.showCreateModal = false
        this.newRate = {
          salesperson: '', rate: '', start_date: '', end_date: '',
        }
        this.loadRates()
      } catch (error) {
        const message = error.response?.data?.detail || 'Erreur lors de la création'
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.creating = false
      }
    },
    async confirmDelete(rate) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Êtes-vous sûr de vouloir supprimer ce taux de commission ?',
        { title: 'Confirmation', okVariant: 'danger', okTitle: 'Supprimer' },
      )
      if (confirmed) {
        try {
          await axios.delete(`/api/commissions/commission-rates/${rate.id}/`)
          this.$bvToast.toast('Taux supprimé', { title: 'Succès', variant: 'success', solid: true })
          this.loadRates()
        } catch (error) {
          const message = error.response?.data?.error || 'Erreur lors de la suppression'
          this.$bvToast.toast(message, { title: 'Erreur', variant: 'danger', solid: true })
        }
      }
    },
    canDelete(rate) {
      const today = new Date().toISOString().split('T')[0]
      return rate.start_date > today
    },
    getStatusVariant(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'secondary'
      if (rate.start_date <= today && rate.end_date >= today) return 'success'
      return 'info'
    },
    getStatusLabel(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'Terminé'
      if (rate.start_date <= today && rate.end_date >= today) return 'Actif'
      return 'À venir'
    },
    formatAmount(value) {
      return parseFloat(value).toFixed(3)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
